import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactAdminModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    instituteName: '',
    contactPerson: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors = {};
    if (!formData?.instituteName?.trim()) newErrors.instituteName = 'Institute name is required';
    if (!formData?.contactPerson?.trim()) newErrors.contactPerson = 'Contact person name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const { supabase } = await import('../../../lib/supabase');

      // Save to database so admin can see it in the dashboard
      const { error: dbError } = await supabase?.from('institute_account_requests')?.insert({
        institute_name: formData?.instituteName,
        contact_person: formData?.contactPerson,
        email: formData?.email,
        phone: formData?.phone || null,
        message: formData?.message || null,
        status: 'pending'
      });

      if (dbError) throw dbError;

      // Also send email notification (best-effort, don't fail if email fails)
      try {
        await supabase?.functions?.invoke('send-email', {
          body: {
            to: 'support@studyexpo.com',
            subject: `Institute Account Request: ${formData?.instituteName}`,
            html: `
              <h2>New Institute Account Request</h2>
              <p><strong>Institute Name:</strong> ${formData?.instituteName}</p>
              <p><strong>Contact Person:</strong> ${formData?.contactPerson}</p>
              <p><strong>Email:</strong> ${formData?.email}</p>
              <p><strong>Phone:</strong> ${formData?.phone || 'Not provided'}</p>
              <p><strong>Message:</strong> ${formData?.message || 'No additional message'}</p>
            `
          }
        });
      } catch (emailErr) {
        console.warn('Email notification failed (request still saved):', emailErr);
      }

      setSubmitted(true);
    } catch (err) {
      setErrors({ general: 'Failed to send request. Please try again or email support@studyexpo.com directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ instituteName: '', contactPerson: '', email: '', phone: '', message: '' });
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Building2" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">Request Institute Account</h2>
              <p className="text-xs text-muted-foreground">Admin will review and get back to you</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <Icon name="CheckCircle" size={40} color="var(--color-success)" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Thank You! 🎉</h3>
              <p className="text-base text-foreground font-medium mb-2">
                Your request has been received!
              </p>
              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                One of our executives will personally reach out to you at{' '}
                <strong className="text-foreground">{formData?.email}</strong> very soon to help you get started.
              </p>
              <p className="text-xs text-muted-foreground mb-6 italic">
                We typically respond within 1–2 business days. We look forward to having you on board! 😊
              </p>
              <Button variant="default" fullWidth onClick={handleClose}>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors?.general && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-error">{errors?.general}</p>
                </div>
              )}
              <Input
                label="Institute Name"
                name="instituteName"
                placeholder="e.g. Delhi University"
                value={formData?.instituteName}
                onChange={handleChange}
                error={errors?.instituteName}
                required
              />
              <Input
                label="Contact Person"
                name="contactPerson"
                placeholder="Your full name"
                value={formData?.contactPerson}
                onChange={handleChange}
                error={errors?.contactPerson}
                required
              />
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="you@institute.edu"
                value={formData?.email}
                onChange={handleChange}
                error={errors?.email}
                required
              />
              <Input
                label="Phone Number (optional)"
                type="tel"
                name="phone"
                placeholder="+91 98765 43210"
                value={formData?.phone}
                onChange={handleChange}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Additional Message <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  name="message"
                  rows={3}
                  placeholder="Tell us about your institute or any specific requirements..."
                  value={formData?.message}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" fullWidth onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="right"
                >
                  {isSubmitting ? 'Sending...' : 'Send Request'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactAdminModal;
